import fullIcons from "@public/full-icons.svg?raw";
import {ref} from "vue";

export const envSettings = {
    IMG_ROOT : import.meta.env.VITE_IMAGE_ROOT,
}

export function injectSvgSprite() {
    const id = '__svg__icons__';
    const existing = document.getElementById(id);
    if (existing) return

    console.log('🔍 Injecting SVG sprite (raw import)');

    const div = document.createElement('div');
    div.id = id;
    div.style.display = 'none';
    div.innerHTML = fullIcons;
    document.body.prepend(div);
}

export function locationToUrl(url, isNewTab = false) {
    if (url) window.open(url, isNewTab ? '_blank' : '_self');
}

export function isMillisUnderDays(millis, days = 1) {
    if (typeof millis !== 'number' || isNaN(millis) || millis < 0) return false;
    const daysInMillis = days * 24 * 60 * 60 * 1000;
    return millis < daysInMillis;
}

export function getSearchParams(key) {
    const params = new URLSearchParams(window.location.search);
    if (key) return params.get(key);
    else {
        // 如果沒有指定 key，返回所有參數的對象
        const allParams = {};
        for (const [k, v] of params.entries()) {
            allParams[k] = v;
        }
        return allParams;
    }
}

export function turboVisit(url) {
    const regex = /\/v2[\/?]/i;
    if (!url) return false;
    regex.test(url) ? Turbo.visit(url) : locationToUrl(url);
}

export async function nextRequestAnimationFrame() {
    return new Promise((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                resolve()
            });
        })
    })
}

export function roundDecimal(value, decimalPlaces = 1) {
    if (typeof value !== 'number' || isNaN(value) || typeof decimalPlaces !== 'number' || isNaN(decimalPlaces) || decimalPlaces < 0) {
        return value;
    }
    const factor = Math.pow(10, decimalPlaces);
    const rounded = Math.round(value * factor) / factor;
    return Number.isInteger(rounded) ? rounded : rounded.toFixed(decimalPlaces);
}

export function convertPercent(decimal, digit = 0) {
    if (typeof decimal !== 'number' || isNaN(decimal)) return 0;
    return roundDecimal(decimal * 100, digit);
}

export function countPercent(current, total) {
    if (typeof current !== 'number' || isNaN(current) || typeof total !== 'number' || isNaN(total) || total <= 0) {
        return 0;
    }
    return Math.round((current / total) * 100);
}

export function cloneObject(obj) {
    if (typeof obj !== 'object' || obj === null) return obj;
    return JSON.parse(JSON.stringify(obj));
}

// Monday to Sunday
export function getCurrentWeekDateStr() {
    const today = new Date();
    const firstDayOfWeek = new Date(today);
    const lastDayOfWeek = new Date(today);

    // Set to the first day of the week (Monday)
    firstDayOfWeek.setDate(today.getDate() - today.getDay() + 1);
    // Set to the last day of the week (Sunday)
    lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);

    const formatDate = (date) => {
        return `${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
    };

    return `${formatDate(firstDayOfWeek)} - ${formatDate(lastDayOfWeek)}`;
}

// HH:mm:ss
export function convertRemaingTimeToText(remainingTime) {
    if (typeof remainingTime !== 'number' || isNaN(remainingTime) || remainingTime < 0) {
        return '';
    }

    const totalSeconds = Math.floor(remainingTime / 1000);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

export function convertToDate(date, type = '', format = '') {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';

    if (format) {
        const rawFormat = format;
        format = format.includes('YYYY') ? format.replace('YYYY', d.getFullYear()) : format;
        format = format.includes('MM') ? format.replace('MM', (d.getMonth() + 1).toString().padStart(2, '0')) : format;
        format = format.includes('DD') ? format.replace('DD', d.getDate().toString().padStart(2, '0')) : format;
        format = format.includes('HH') ? format.replace('HH', d.getHours().toString().padStart(2, '0')) : format;
        format = format.includes('mm') ? format.replace('mm', d.getMinutes().toString().padStart(2, '0')) : format;
        format = format.includes('ss') ? format.replace('ss', d.getSeconds().toString().padStart(2, '0')) : format;
        if ( format !== rawFormat ) {
            return format;
        }
    }

    switch (type) {
        case 'full':
            // 返回 YYYY-MM-DD HH:mm:ss
            return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;

        default:
            // 默认为 MM/DD HH:mm
            return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours()}:${d.getMinutes().toString().padStart(2, '0')}`;
    }
}

export function convertDuration(duration) {
    if (typeof duration !== 'number' || isNaN(duration) || duration < 0) {
        return { min: null, sec: null };
    }
    duration = roundDecimal(duration, 0);
    const min = Math.floor(duration / 60);
    const sec = duration % 60;
    return { min, sec };
}

export function getFooterStr(style) {
    return style === 'V2-LITE' ? 'Lite' : 'Pro'
}

export function isOldOS() {
    const ua = navigator.userAgent;

    // 檢查 iOS
    const isiOS = /iP(hone|od|ad)/.test(ua);
    if (isiOS) {
        const match = ua.match(/OS (\d+)[._]?/); // 只取主版本號
        return match ? parseInt(match[1], 10) < 17 : false;
    }

    // 檢查 Android
    const matchAndroid = ua.match(/Android\s(\d+)/);
    if (matchAndroid) {
        return parseInt(matchAndroid[1], 10) < 10;
    }

    // 其他裝置或無法判斷，視為過關
    return false;
}
